import { EquityFieldKey } from 'types';
import {
  OptionType,
  StrategyDefinition,
  StrategyLegs,
  StrategyType,
} from 'types/optionsCalculator';
import AccessTime from '@mui/icons-material/AccessTime';
import Block from '@mui/icons-material/Block';
import ShowChart from '@mui/icons-material/ShowChart';
import TrendingDown from '@mui/icons-material/TrendingDown';
import TrendingFlat from '@mui/icons-material/TrendingFlat';
import TrendingUp from '@mui/icons-material/TrendingUp';
import AllInclusive from '@mui/icons-material/AllInclusive';

export const OPTIONS_CALCULATOR_ALL_FIELDS: EquityFieldKey[] = [
  EquityFieldKey.prevClose,
  EquityFieldKey.earningsDate,
  EquityFieldKey.wkHigh52,
  EquityFieldKey.wkLow52,
  EquityFieldKey.callVolume,
  EquityFieldKey.putVolume,
  EquityFieldKey.skew,
  EquityFieldKey.iv30,
  EquityFieldKey.rv30,
  EquityFieldKey.ivRank,
  EquityFieldKey.iv_pct,
  EquityFieldKey.callsum,
  EquityFieldKey.putsum,
  EquityFieldKey.putCallRatio,
  EquityFieldKey.nextExpGamma,
  EquityFieldKey.nextExpDelta,
  EquityFieldKey.topGammaExp,
  EquityFieldKey.topDeltaExp,
  EquityFieldKey.callGamma,
  EquityFieldKey.putGamma,
  EquityFieldKey.gammaRatio,
  EquityFieldKey.callDelta,
  EquityFieldKey.putDelta,
  EquityFieldKey.deltaRatio,
  EquityFieldKey.neSkew,
  EquityFieldKey.stock_volume,
  EquityFieldKey.skewRank,
  EquityFieldKey.garchRank,
  EquityFieldKey.cskew_pct,
  EquityFieldKey.pskew_pct,
];

export const STRATEGY_CONFIGS: StrategyLegs = {
  [StrategyType.LONG_CALL]: [
    { delta: 0.5, dte: 30, quantity: 1, optionType: OptionType.CALL },
  ],
  [StrategyType.LONG_PUT]: [
    { delta: 0.5, dte: 30, quantity: 1, optionType: OptionType.PUT },
  ],
  [StrategyType.LONG_CALL_SPREAD]: [
    { delta: 0.2, dte: 30, quantity: 1, optionType: OptionType.CALL },
    { delta: 0.1, dte: 30, quantity: -1, optionType: OptionType.CALL },
  ],
  [StrategyType.LONG_PUT_SPREAD]: [
    { delta: 0.2, dte: 30, quantity: 1, optionType: OptionType.PUT },
    { delta: 0.1, dte: 30, quantity: -1, optionType: OptionType.PUT },
  ],
  [StrategyType.SHORT_CALL_SPREAD]: [
    { delta: 0.2, dte: 30, quantity: -1, optionType: OptionType.CALL },
    { delta: 0.1, dte: 30, quantity: 1, optionType: OptionType.CALL },
  ],
  [StrategyType.SHORT_PUT_SPREAD]: [
    { delta: 0.2, dte: 30, quantity: -1, optionType: OptionType.PUT },
    { delta: 0.1, dte: 30, quantity: 1, optionType: OptionType.PUT },
  ],
  [StrategyType.IRON_CONDOR]: [
    {
      delta: 0.1,
      dte: 30,
      quantity: 1,
      optionType: OptionType.CALL,
      mirror: { optionType: OptionType.PUT, target: 'fwdPrice' },
    },
    {
      delta: 0.2,
      dte: 30,
      quantity: -1,
      optionType: OptionType.CALL,
      mirror: { optionType: OptionType.PUT, target: 'fwdPrice' },
    },
  ],
  [StrategyType.IRON_FLY]: [
    {
      delta: 0.5,
      dte: 30,
      quantity: -1,
      optionType: OptionType.CALL,
      mirror: { optionType: OptionType.PUT, target: { index: 0 } },
    },
    {
      delta: 0.2,
      dte: 30,
      quantity: 1,
      optionType: OptionType.CALL,
      mirror: { optionType: OptionType.PUT, target: 'fwdPrice' },
    },
  ],
  [StrategyType.LONG_STRADDLE]: [
    { delta: 0.5, dte: 30, quantity: 1, optionType: OptionType.CALL },
    { delta: 0.5, dte: 30, quantity: 1, optionType: OptionType.PUT },
  ],
  [StrategyType.CALL_CALENDAR_SPREAD]: [
    { delta: 0.2, dte: 30, quantity: -1, optionType: OptionType.CALL },
    { delta: 0.2, dte: 90, quantity: 1, optionType: OptionType.CALL },
  ],
  [StrategyType.PUT_CALENDAR_SPREAD]: [
    { delta: 0.2, dte: 30, quantity: -1, optionType: OptionType.PUT },
    { delta: 0.2, dte: 90, quantity: 1, optionType: OptionType.PUT },
  ],
  [StrategyType.CALL_DIAGONAL]: [
    { delta: 0.2, dte: 30, quantity: -1, optionType: OptionType.CALL },
    { delta: 0.5, dte: 90, quantity: 1, optionType: OptionType.CALL },
  ],
  [StrategyType.PUT_DIAGONAL]: [
    { delta: 0.2, dte: 30, quantity: -1, optionType: OptionType.PUT },
    { delta: 0.5, dte: 90, quantity: 1, optionType: OptionType.PUT },
  ],
  [StrategyType.CALL_VERTICAL]: [
    { delta: 0.2, dte: 30, quantity: 1, optionType: OptionType.CALL },
  ],
  [StrategyType.PUT_VERTICAL]: [
    { delta: 0.2, dte: 30, quantity: 1, optionType: OptionType.PUT },
  ],
  [StrategyType.CALL_FLY]: [
    { delta: 0.5, dte: 30, quantity: -2, optionType: OptionType.CALL }, // ATM center
    {
      delta: 0.2, // 20 delta OTM
      dte: 30,
      quantity: 1,
      optionType: OptionType.CALL,
      // mirroring the 20 delta around the ATM strike
      mirror: { optionType: OptionType.CALL, target: { index: 0 } },
    },
  ],
  [StrategyType.PUT_FLY]: [
    { delta: 0.5, dte: 30, quantity: -2, optionType: OptionType.PUT }, // ATM center
    {
      delta: 0.2, // 20 delta OTM
      dte: 30,
      quantity: 1,
      optionType: OptionType.PUT,
      // mirroring the 20 delta around the ATM strike
      mirror: { optionType: OptionType.PUT, target: { index: 0 } },
    },
  ],
};

export const OC_STRATEGIES: StrategyDefinition[] = [
  {
    id: StrategyType.LONG_CALL,
    name: 'Long Call',
    description:
      'Bullish strategy where you buy the right to purchase stock at a fixed price. Profit from upward price movement with unlimited upside potential while risk is limited to the premium paid. Time decay and volatility changes affect option value.',
    tags: [
      { icon: <TrendingUp />, text: 'Bullish' },
      { icon: <AllInclusive />, text: 'Unlimited' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Directional',
    riskLevel: 'Medium',
    legs: [{ type: 'call', action: 'buy', delta: 20, dte: 30 }],
  },
  {
    id: StrategyType.LONG_PUT,
    name: 'Long Put',
    description:
      'Bearish strategy where you sell the right to sell stock at a fixed price. Profit from downward price movement with unlimited downside potential while risk is limited to the premium received. Time decay and volatility changes affect option value.',
    tags: [
      { icon: <TrendingDown />, text: 'Bearish' },
      { icon: <TrendingUp />, text: 'High' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Directional',
    riskLevel: 'Medium',
    legs: [{ type: 'put', action: 'buy', delta: 20, dte: 30 }],
  },
  {
    id: StrategyType.LONG_CALL_SPREAD,
    name: 'Long Call Spread',
    description:
      'A debit spread strategy with a bullish outlook that involves buying a lower strike call and selling a higher strike call with the same expiration. Maximum profit occurs when the underlying closes above the long strike at expiration. Both risk and reward are capped, making it a defined-risk strategy. Time decay works against the position but is partially offset by the short call.',
    tags: [
      { icon: <TrendingUp />, text: 'Bullish' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Directional',
    riskLevel: 'Low',
    legs: [
      { type: 'call', action: 'buy', delta: 10, dte: 30 },
      { type: 'call', action: 'sell', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.LONG_PUT_SPREAD,
    name: 'Long Put Spread',
    description:
      'A debit spread with bearish sentiment that combines buying a higher strike put and selling a lower strike put with identical expiration dates. Optimal outcome occurs when the stock price falls below the long put strike through expiration. This defined-risk approach limits both potential gains and losses. Time decay works against the position but is partially offset by the short put.',
    tags: [
      { icon: <TrendingDown />, text: 'Bearish' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Directional',
    riskLevel: 'Low',
    legs: [
      { type: 'put', action: 'buy', delta: 10, dte: 30 },
      { type: 'put', action: 'sell', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.SHORT_CALL_SPREAD,
    name: 'Short Call Spread',
    description:
      'A credit spread strategy with a bearish outlook that involves selling a lower strike call and buying a higher strike call with the same expiration. Maximum profit occurs when the underlying closes below the short strike at expiration. Both risk and reward are capped, making it a defined-risk strategy. Time decay benefits the position given minimum downside stock movement.',
    tags: [
      { icon: <TrendingDown />, text: 'Bearish' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Directional',
    riskLevel: 'Low',
    legs: [
      { type: 'call', action: 'sell', delta: 10, dte: 30 },
      { type: 'call', action: 'buy', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.SHORT_PUT_SPREAD,
    name: 'Short Put Spread',
    description:
      'A credit spread with bullish sentiment that combines selling a higher strike put and purchasing a lower strike put with identical expiration dates. Optimal outcome occurs when the stock price remains above the short put strike through expiration. This defined-risk approach limits both potential gains and losses. Time decay benefits the position given minimum downside stock movement.',
    tags: [
      { icon: <TrendingUp />, text: 'Bullish' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Directional',
    riskLevel: 'Low',
    legs: [
      { type: 'put', action: 'sell', delta: 10, dte: 30 },
      { type: 'put', action: 'buy', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.IRON_CONDOR,
    name: 'Iron Condor',
    description:
      'A market-neutral credit strategy that profits when the underlying asset trades within a specific price range. Created by combining a bull put spread and bear call spread, it generates income upfront with maximum profit achieved when the stock closes between the inner strikes at expiration. Lower volatility expands the profit zone, while higher volatility contracts it. Time decay assists profitable positions but works against losing ones.',
    tags: [
      { icon: <TrendingFlat />, text: 'Neutral' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Neutral',
    riskLevel: 'Medium',
    legs: [
      { type: 'call', action: 'sell', delta: 10, dte: 30 },
      { type: 'call', action: 'buy', delta: 20, dte: 30 },
      { type: 'put', action: 'sell', delta: 10, dte: 30 },
      { type: 'put', action: 'buy', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.IRON_FLY,
    name: 'Iron Butterfly',
    description:
      'A precision neutral strategy that offers substantial profit potential when the underlying remains close to the center strike price. Constructed by selling both a call and put at the same strike while buying protective options on either side. This credit strategy thrives on low volatility and benefits from time decay when positioned correctly. Maximum reward occurs with pinpoint accuracy at the short strike, making it a high-reward but challenging strategy to execute successfully.',
    tags: [
      { icon: <TrendingFlat />, text: 'Neutral' },
      { icon: <TrendingUp />, text: 'High' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Neutral',
    riskLevel: 'High',
    legs: [
      { type: 'call', action: 'sell', delta: 50, dte: 30 },
      { type: 'call', action: 'buy', delta: 20, dte: 30 },
      { type: 'put', action: 'sell', delta: 50, dte: 30 },
      { type: 'put', action: 'buy', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.CALL_CALENDAR_SPREAD,
    name: 'Call Calendar Spread',
    description:
      'A time-based strategy that capitalizes on the difference in time decay rates between near-term and longer-term options at the same strike price. Involves selling a short-term call while simultaneously purchasing a longer-dated call. Peak profitability occurs when the stock hovers around the strike price as the front-month option approaches expiration. Strike selection can be adjusted to introduce directional bias - choose at-the-money for neutral positioning.',
    tags: [
      { icon: <AccessTime />, text: 'Time' },
      { icon: <TrendingFlat />, text: 'Neutral' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Volatility',
    riskLevel: 'Medium',
    legs: [
      { type: 'call', action: 'buy', delta: 20, dte: 30 },
      { type: 'call', action: 'sell', delta: 20, dte: 90 },
    ],
  },
  {
    id: StrategyType.PUT_CALENDAR_SPREAD,
    name: 'Put Calendar Spread',
    description:
      'A time-based strategy that capitalizes on the difference in time decay rates between near-term and longer-term options at the same strike price. This strategy involves selling a short-dated put while simultaenously purchasing a longer-dated put. Peak profitability occurs when the stock hovers around the strike price as the shorter-dated put approaches expiration. Strike selection can be adjusted to introduce directiona bias - choose at-the-money for neutral positioning.',
    tags: [
      { icon: <AccessTime />, text: 'Time' },
      { icon: <TrendingFlat />, text: 'Neutral' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Volatility',
    riskLevel: 'Medium',
    legs: [
      { type: 'put', action: 'buy', delta: 20, dte: 30 },
      { type: 'put', action: 'sell', delta: 20, dte: 90 },
    ],
  },
  {
    id: StrategyType.CALL_DIAGONAL,
    name: 'Call Diagonal',
    description:
      'A variation of the calendar spread where the long call has a lower strike price and later expiration, creating a bullish bias. This strategy benefits from both time decay and upward price movement. The short call expires first, allowing the trader to potentially roll it while maintaining the longer-term long position. It provides similar exposure to owning stock with a covered call but requires less capital.',
    tags: [
      { icon: <TrendingUp />, text: 'Bullish' },
      { icon: <ShowChart />, text: 'Diagonal' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Volatility',
    riskLevel: 'Medium',
    legs: [
      { type: 'call', action: 'buy', delta: 50, dte: 90 },
      { type: 'call', action: 'sell', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.PUT_DIAGONAL,
    name: 'Put Diagonal',
    description:
      'A modified calendar spread structure where the longer-dated put is positioned deeper in-the-money compared to the shorter-term put, creating an altered risk-reward profile. This configuration introduces a bearish directional bias while still benefiting from time decay differentials between the two expiration cycles.',
    tags: [
      { icon: <TrendingDown />, text: 'Bearish' },
      { icon: <ShowChart />, text: 'Diagonal' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Volatility',
    riskLevel: 'Medium',
    legs: [
      { type: 'put', action: 'buy', delta: 50, dte: 90 },
      { type: 'put', action: 'sell', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.LONG_STRADDLE,
    name: 'Long Straddle',
    description:
      'This options strategy combines one ATM call with one ATM put. "Going long a straddle" means this position benefits when implied volatility increases, but time decay works against this strategy',
    tags: [
      { icon: <AccessTime />, text: 'Time' },
      { icon: <ShowChart />, text: 'Diagonal' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Volatility',
    riskLevel: 'Medium',
    legs: [
      { type: 'call', action: 'buy', delta: 50, dte: 30 },
      { type: 'put', action: 'buy', delta: 50, dte: 30 },
    ],
  },
  {
    id: StrategyType.CALL_FLY,
    name: 'Call Fly',
    description:
      'This neutral options strategy combines buying one lower-strike call, selling two middle-strike calls, and buying one higher-strike call, all with the same expiration. It offers an attractive cost-effective approach with favorable risk-to-reward characteristics. The position benefits when implied volatility contracts, expanding the profit zone, while rising volatility compresses the profitable price range. Time decay works in your favor when the underlying trades near the center strike, but against you when positioned outside the wings.',
    tags: [
      { icon: <TrendingFlat />, text: 'Neutral' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Neutral',
    riskLevel: 'Medium',
    legs: [
      { type: 'call', action: 'buy', delta: 80, dte: 30 },
      { type: 'call', action: 'sell', delta: 50, dte: 30 },
      { type: 'call', action: 'sell', delta: 50, dte: 30 },
      { type: 'call', action: 'buy', delta: 20, dte: 30 },
    ],
  },
  {
    id: StrategyType.PUT_FLY,
    name: 'Put Fly',
    description:
      'This market-neutral strategy involves purchasing one higher-strike put, selling two middle-strike puts, and purchasing one lower-strike put, creating an inexpensive position with excellent profit potential relative to risk. The trade thrives when volatility decreases, widening the breakeven points, while volatility expansion reduces the profitable territory. Theta works favorably when the stock price hovers around the short strike, but becomes detrimental when the underlying moves beyond the long strikes.',
    tags: [
      { icon: <TrendingFlat />, text: 'Neutral' },
      { icon: <Block />, text: 'Limited' },
    ],
    category: 'Neutral',
    riskLevel: 'Medium',
    legs: [
      { type: 'put', action: 'buy', delta: 80, dte: 30 },
      { type: 'put', action: 'sell', delta: 50, dte: 30 },
      { type: 'put', action: 'sell', delta: 50, dte: 30 },
      { type: 'put', action: 'buy', delta: 20, dte: 30 },
    ],
  },
];

// Configuration for DTE-based tick intervals
// Map of [minDays, stepHours] - when DTE >= minDays, use stepHours interval
export const DEFAULT_DTE_PNL_TICK_CONFIG = new Map([
  [10, 24], // >= 10 days, 24 hours
  [2, 2], // >= 2 days, 2 hours
  [1, 1], // >= 1 day, 1 hour
  [0, 0.5], // < 1 day, 30 minutes
]);
